/**
 * The Community's Lessons tab (2026-10-25, Claude outputs/community-simplify-
 * 2026-10-25/DESIGN.md §3; redone 2026-10-28, lesson-redo DESIGN.md A, C).
 * One centred search for lessons and Mystics, Around the fire, one stream of
 * LessonCards. The words live here so the page and its specs share them.
 */

export const TAB_LABEL = 'Lessons'
export const HEAD = {
  title: 'Shared Amongst Humanity',
  line: 'Lessons people chose to pass on.',
}
export const SEARCH_LABEL = 'Search lessons and Mystics'
// The resting field cycles through recent public lesson questions (C2).
export const SEARCH_PLACEHOLDER = SEARCH_LABEL
export const PLACEHOLDER_EVERY = 3200
// The question keeps its own case (lesson walk B7). The parts let the field
// cut the question alone, so a long one ends "…”" with the quote kept.
export function placeholderParts(question) {
  const q = String(question || '').trim().replace(/[.?!]+$/, '')
  return q ? { pre: 'Try “', q, post: '”' } : { pre: '', q: SEARCH_LABEL, post: '' }
}
export function placeholderLine(question) {
  const p = placeholderParts(question)
  return `${p.pre}${p.q}${p.post}`
}
export const NEWEST = 'Newest lessons'
export const MORE = 'More lessons'
export const MOST_SPOKE = 'Lessons that spoke to the most people'
// The feed's bottom (community-feed-invite DESIGN.md §3): the same search
// again, and under its results, always, "✦ Ask {Mystic} about “{their words}”".
export const FOOT_LINE = 'Looking for something?'
export const FOOT_ASK_MAX = 40
/** The words in the pill: trimmed, spaces collapsed, up to 40, cut at a word (if that keeps more than 20), then "…". */
export function footWords(words) {
  const w = String(words || '').trim().replace(/\s+/g, ' ')
  if (w.length <= FOOT_ASK_MAX) return w
  const cut = w.slice(0, FOOT_ASK_MAX)
  const sp = cut.lastIndexOf(' ')
  return (sp > 20 ? cut.slice(0, sp) : cut).replace(/[\s,.;:!?]+$/, '') + '…'
}
export const footAsk = (name, words) => `✦ Ask ${name || 'Mystic Aida'} about “${footWords(words)}”`
// No lesson: "✦ Ask McKenzie about it", one line (C5).
export const ASK = {
  button: (name) => `✦ Ask ${name || 'Mystic Aida'} about it`,
}
// Mystic Aida reads for a visitor who asks from the Community.
export const ASK_READER = 'iam'

/** The seven topics (DESIGN §4.1, build/API.md §1): the keys the server stores as share_topic. */
export const TOPICS = [
  { key: 'love', label: 'Love' },
  { key: 'money', label: 'Money' },
  { key: 'work', label: 'Work' },
  { key: 'health', label: 'Health' },
  { key: 'family', label: 'Friends & family' },
  { key: 'self', label: 'Self' },
  { key: 'choices', label: 'Choices' },
]
export const ALL = { key: '', label: 'All' }

export function topicLabel(key) {
  const t = TOPICS.find((x) => x.key === key)
  return t ? t.label : ''
}

/** A topic as words in a sentence: "money", "friends & family". */
const topicWords = (key) => topicLabel(key).toLowerCase()

/**
 * The line over results (C2): "17 lessons about love · 3 Mystics". A search
 * the server understood as a topic names the topic; one that matched only
 * the text reads 3 lessons for “tended”.
 */
export function resultLine({ count, topic, query, mystics = 0 }) {
  const n = Number(count) || 0
  const noun = n === 1 ? 'lesson' : 'lessons'
  const about = topic && topicLabel(topic) ? `about ${topicWords(topic)}` : `for “${String(query || '').trim()}”`
  const m = Number(mystics) || 0
  return { count: n, noun, about, mystics: m ? ` · ${m} Mystic${m === 1 ? '' : 's'}` : '' }
}

/** The no-match line (C2): No lesson about “birth chart” yet. */
export function noMatchTitle({ query, topic }) {
  const q = String(query || '').trim()
  if (q) return `No lesson about “${q}” yet.`
  return `No lesson about ${topicWords(topic) || 'this'} yet.`
}

/** Who "Ask … about it" names: the top Mystic who matched and isn't the house, else Mystic Aida. */
export function askerOf(mystics) {
  const m = (Array.isArray(mystics) ? mystics : []).find((x) => x && x.sub_domain && x.sub_domain !== ASK_READER)
  return m ? { sub: m.sub_domain, name: m.name || 'Mystic Aida' } : { sub: ASK_READER, name: 'Mystic Aida' }
}

/** A Mystic result's one reason line; the server sends it without the ✦. */
export const whyLine = (why) => (String(why || '').trim() ? `✦ ${String(why).trim()}` : '')

/** The snippet with its matched words lit (gold bold): text parts, never HTML. */
export function snippetParts(text, words) {
  const t = String(text || '')
  const ws = (Array.isArray(words) ? words : []).map((w) => String(w || '').trim()).filter(Boolean)
  if (!t || !ws.length) return t ? [{ text: t, hit: false }] : []
  const esc = (w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(${ws.map(esc).join('|')})`, 'gi')
  return t.split(re).filter((x) => x !== '').map((x) => ({ text: x, hit: ws.some((w) => w.toLowerCase() === x.toLowerCase()) }))
}

/** Around the fire's stat tile (D5): the topic as the word; a tap searches it. */
export function fireStat(stat) {
  if (!stat) return null
  const word = topicLabel(stat.topic) || String(stat.label || '').trim()
  return word ? { word, label: 'Most asked about' } : null
}

// ── Recent searches: on this device only, never sent (C2). Every access in try/catch. ──
export const RECENT_KEY = 'ma_lesson_recent_searches'
const RECENT_MAX = 5
export function readRecent() {
  try {
    const v = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]')
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string' && x.trim()).slice(0, RECENT_MAX) : []
  } catch (e) { return [] }
}
function writeRecent(list) {
  try { window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX))) } catch (e) { /* private mode, full */ }
}
export function rememberSearch(q) {
  const w = String(q || '').trim()
  if (w.length < 2) return readRecent()
  const list = [w, ...readRecent().filter((x) => x.toLowerCase() !== w.toLowerCase())].slice(0, RECENT_MAX)
  writeRecent(list)
  return list
}
export function forgetSearch(q) {
  const list = readRecent().filter((x) => x !== q)
  writeRecent(list)
  return list
}
export function clearRecent() {
  try { window.localStorage.removeItem(RECENT_KEY) } catch (e) { /* nothing kept */ }
  return []
}

/** What "Ask the cards about it" seeds the reading with: the visitor's own words. */
export function askWords({ query, topic }) {
  const q = String(query || '').trim()
  return q || topicLabel(topic)
}

/** "More about money" under a topic's results, "More lessons" in the stream. */
export function moreLabel(topic) {
  return topic ? `More about ${topicWords(topic)}` : MORE
}

/**
 * The two Fireside tiles (DESIGN §3.4): the day's vibe and the most-drawn
 * card, from covenant-overview's `vibe` and `card`. No rotation, no bullets.
 */
export function fireTiles(vibe, card) {
  const out = []
  if (vibe && vibe.title) out.push({ key: 'vibe', title: vibe.title, sub: 'Today’s questions', image_url: vibe.image_url || '', pulse: vibe })
  if (card && card.title) out.push({ key: 'card', title: card.title, sub: 'Most drawn card', image_url: card.image_url || card.card_image_url || '', pulse: card })
  return out
}

export function fireTitle(date = new Date()) {
  return `✦ Around the fire · ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
}

// ── A quiet line over the Lessons (K2): a deep link to a lesson that isn't available. ──
let pendingNotice = ''
export const NOTICE_EVENT = 'lessons-notice'
export function setLessonsNotice(text) {
  pendingNotice = String(text || '')
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(NOTICE_EVENT, { detail: { text: pendingNotice } }))
}
/** The notice waiting for the tab (read once). */
export function takeLessonsNotice() {
  const t = pendingNotice
  pendingNotice = ''
  return t
}

// ── The invitation card at a page seam (community-feed-invite DESIGN.md §4.1) ──
// Three lines and two taps; no pronouns, no prices, no sign-up words.
export const INVITE_TOPIC = {
  love: { about: 'love', title: 'A love question of your own?' },
  money: { about: 'money', title: 'A money question of your own?' },
  work: { about: 'work', title: 'A work question of your own?' },
  health: { about: 'health', title: 'A health question of your own?' },
  family: { about: 'friends and family', title: 'A question about friends or family?' },
  self: { about: 'the self', title: 'A question about yourself?' },
  choices: { about: 'choices', title: 'A choice of your own to make?' },
}
const NUM = ['zero', 'one', 'two', 'three', 'four', 'five', 'six']
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1)
/** "Three lessons about work in a row." or "Four of the last five lessons were about money." */
export function inviteCount({ topic, row, n, m }) {
  const t = INVITE_TOPIC[topic]
  const about = t ? t.about : topicWords(topic)
  return row >= 3 ? `${cap(NUM[row] || String(row))} lessons about ${about} in a row.` : `${cap(NUM[n] || String(n))} of the last ${NUM[m] || m} lessons were about ${about}.`
}
// The button reads "✦ Ask {name}": the ✦ is drawn apart so it can turn once.
export const INVITE_ASK = (name) => `Ask ${name || 'Mystic Aida'}`
// The portrait and the name: one button, its accessible name.
export const INVITE_MEET = (name) => `Meet ${name || 'Mystic Aida'}`
